import { useEffect, useMemo, useRef, useState } from 'react';
import { MarkerClusterer, type Cluster } from '@googlemaps/markerclusterer';
import { cn } from '@/lib/utils';
import { loadGoogleMaps, getGoogleMapsKey } from '@/lib/google-maps';

export type MarkerIcon = 'property' | 'device' | 'gateway' | 'dot';

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  label?: string;
  // `slate` is reserved for gateway/infrastructure pins so yellow can
  // keep meaning "warning" (e.g., offline-devices on a property pin)
  // without overloading the meaning.
  color?: 'red' | 'green' | 'yellow' | 'blue' | 'gray' | 'slate';
  icon?: MarkerIcon;
}

interface GoogleMapProps {
  markers: MapMarker[];
  center?: { lat: number; lng: number };
  zoom?: number;
  onMarkerClick?: (id: string) => void;
  className?: string;
  // When true, viewport recomputes on every marker/center/zoom change.
  // Default (false) keeps the viewport sticky after the first render so
  // background polling refetches don't yank the user out of their pan/zoom
  // — fixes the May 14 batch "map keeps re-centering" complaint on the
  // Property Portal and Dashboard maps. Callers that need legacy
  // auto-fit-to-data behavior can opt in.
  recenter?: boolean;
}

const PIN_COLORS: Record<string, string> = {
  red: '#EF4444',
  green: '#22C55E',
  yellow: '#EAB308',
  blue: '#318CDD',
  gray: '#9CA3AF',
  // Brand dark slate-blue — matches the email-header gradient and
  // sidebar accents. Used for gateway pins so they read as
  // "infrastructure" rather than warning state (which yellow implied).
  slate: '#1E3A5F',
};


function getIconPath(icon: MarkerIcon): string {
  switch (icon) {
    case 'property':
      return '<path d="M3 21V9l9-6 9 6v12H3z" fill="none" stroke="white" stroke-width="2"/><path d="M9 21v-6h6v6" fill="none" stroke="white" stroke-width="2"/>';
    case 'gateway':
      // Lucide `RadioTower` path data — matches the sidebar Gateways
      // icon (`sidebar-layout.tsx:99`) so the on-map glyph is the same
      // tower users see in the rest of the UI. Yellow background and
      // outer pin circle remain (set by `createSvgDataUrl`).
      return '<g fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M4.9 16.1C1 12.2 1 5.8 4.9 1.9"/>'
        + '<path d="M7.8 4.7a6.14 6.14 0 0 0-.8 7.5"/>'
        + '<circle cx="12" cy="9" r="2"/>'
        + '<path d="M16.2 4.8c2 2 2.26 5.11.8 7.47"/>'
        + '<path d="M19.1 1.9a9.96 9.96 0 0 1 0 14.1"/>'
        + '<path d="M9.5 18h5"/>'
        + '<path d="m8 22 4-11 4 11"/>'
        + '</g>';
    case 'device':
      return '<rect x="6" y="3" width="12" height="18" rx="2" fill="none" stroke="white" stroke-width="1.5"/><circle cx="12" cy="17" r="1" fill="white"/>';
    default:
      return '';
  }
}

function createSvgDataUrl(color: string, icon: MarkerIcon): string {
  if (icon === 'dot') {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"><circle cx="7" cy="7" r="6" fill="${color}" stroke="white" stroke-width="2"/></svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

  const innerIcon = getIconPath(icon);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32">
    <circle cx="16" cy="16" r="15" fill="${color}" stroke="white" stroke-width="2"/>
    <svg x="8" y="8" width="16" height="16" viewBox="0 0 24 24">${innerIcon}</svg>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function createClusterIcon(count: number): string {
  // Stepped sizing: small for a few, larger for many. Keeps clusters readable
  // without dominating the map.
  const size = count < 10 ? 36 : count < 50 ? 44 : 52;
  const fontSize = count < 10 ? 12 : count < 50 ? 13 : 14;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="#318CDD" fill-opacity="0.25" />
    <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 6}" fill="#318CDD" />
    <text x="50%" y="50%" text-anchor="middle" dominant-baseline="central" fill="white" font-family="system-ui,-apple-system,sans-serif" font-weight="600" font-size="${fontSize}">${count}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export function GoogleMap({
  markers,
  center,
  zoom = 10,
  onMarkerClick,
  className,
  recenter = false,
}: GoogleMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!getGoogleMapsKey()) { setError(true); return; }
    loadGoogleMaps()
      .then(() => setReady(true))
      .catch(() => setError(true));
  }, []);

  // Stable initial viewport — computed once per mount from the first
  // markers/center value. The viewport-init effect captures this so the
  // map opens at the right place even when the parent rerenders with a
  // fresh marker array on every poll tick.
  const initialViewport = useMemo(() => {
    if (center) return { center, fitMarkers: markers };
    if (markers.length > 0) {
      return {
        center: {
          lat: markers.reduce((s, m) => s + m.lat, 0) / markers.length,
          lng: markers.reduce((s, m) => s + m.lng, 0) / markers.length,
        },
        fitMarkers: markers,
      };
    }
    return { center: { lat: 39.8283, lng: -98.5795 }, fitMarkers: [] as MapMarker[] };
    // Intentionally empty-deps: viewport is captured once on mount and the
    // marker-update effect below handles all subsequent marker churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect 1 — viewport init. Runs once after `ready` flips so the map
  // mounts at the right center/zoom and (when applicable) fits the
  // initial bounds. Marker churn from polling does NOT re-fire this.
  useEffect(() => {
    if (!ready || !mapRef.current || mapInstanceRef.current) return;

    mapInstanceRef.current = new google.maps.Map(mapRef.current, {
      center: initialViewport.center,
      zoom,
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ],
    });

    if (initialViewport.fitMarkers.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      initialViewport.fitMarkers.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
      mapInstanceRef.current.fitBounds(bounds, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Effect 2 — opt-in recenter on data change. Legacy callers that want
  // the viewport to follow the markers (Dashboard before May 14, e.g.)
  // can pass `recenter` to restore that behavior. Default (false) is
  // sticky: user pan/zoom survives marker refetches.
  useEffect(() => {
    if (!recenter || !mapInstanceRef.current) return;
    if (center) {
      mapInstanceRef.current.setCenter(center);
    }
    if (markers.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
      mapInstanceRef.current.fitBounds(bounds, 50);
    } else if (markers.length === 1) {
      mapInstanceRef.current.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
    }
  }, [recenter, center, markers]);

  // Effect 3 — marker layer updates. Re-runs whenever markers/onMarkerClick
  // change so add/remove/relabel propagates, but never touches the
  // map's center or zoom. Without this split the previous single-effect
  // would call setCenter / fitBounds on every poll tick, undoing the
  // user's pan/zoom (the May 14 zoom-reset bug).
  useEffect(() => {
    if (!ready || !mapInstanceRef.current) return;

    // Tear down prior marker + clusterer state before re-rendering
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    // Build markers (not yet attached to map — clusterer manages their visibility)
    markers.forEach((m) => {
      const pinColor = PIN_COLORS[m.color ?? 'blue'];
      const iconType = m.icon ?? 'dot';
      const size = iconType === 'dot' ? 14 : 32;

      const svgIcon: google.maps.Icon = {
        url: createSvgDataUrl(pinColor, iconType),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
      };

      const marker = new google.maps.Marker({
        position: { lat: m.lat, lng: m.lng },
        title: m.label,
        icon: svgIcon,
      });

      if (onMarkerClick) {
        marker.addListener('click', () => onMarkerClick(m.id));
      }

      markersRef.current.push(marker);
    });

    // Cluster markers that are visually close. Clicking a cluster zooms to fit
    // its members; once zoomed in enough, clusters break apart into individual
    // markers that fire their own click handlers.
    if (markersRef.current.length > 0) {
      clustererRef.current = new MarkerClusterer({
        map: mapInstanceRef.current,
        markers: markersRef.current,
        renderer: {
          render: ({ count, position }: Cluster) => {
            return new google.maps.Marker({
              position,
              icon: {
                url: createClusterIcon(count),
                scaledSize: new google.maps.Size(36, 36),
                anchor: new google.maps.Point(18, 18),
              },
              label: undefined,
              zIndex: 1000 + count,
            });
          },
        },
      });
    }
  }, [ready, markers, onMarkerClick]);

  if (error || !getGoogleMapsKey()) {
    return (
      <div className={cn('flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-xl text-sm text-gray-400', className)}>
        Map unavailable
      </div>
    );
  }

  return <div ref={mapRef} className={cn('rounded-xl overflow-hidden', className)} />;
}
